import { useLayoutEffect, useState } from 'react';
import { parseAnchorId } from 'utils/helpers/scrollHelpers';

const clamp = (value: number) => Math.max(0, value);

const isBetween = (value: number, floor: number, ceil: number) =>
  value >= floor && value <= ceil;

/**
 * useScrollSpy is a hook that will return the id of the element that is currently in the viewport
 */
export const useScrollSpy = (
  ids: (string | undefined)[],
  offset: number = 0,
  spyFn: Function = isBetween,
) => {
  const [activeId, setActiveId] = useState('');

  useLayoutEffect(() => {
    const listener = () => {
      const scroll = window.scrollY;

      const position = ids
        .map((id) => {
          const element = document.getElementById(parseAnchorId(id));
          if (!element) return { id, top: -1, bottom: -1 };

          const rect = element.getBoundingClientRect();
          const top = clamp(rect.top + scroll - offset);
          const bottom = clamp(rect.bottom + scroll - offset);
          return { id, top, bottom };
        })
        .find(({ top, bottom }) => spyFn(scroll, top, bottom));
      setActiveId(position?.id || '');
    };

    if (ids.length === 0) return () => {};
    listener();

    window.addEventListener('resize', listener);
    window.addEventListener('scroll', listener);

    return () => {
      window.removeEventListener('resize', listener);
      window.removeEventListener('scroll', listener);
    };
  }, [ids, offset, spyFn]);

  return activeId;
};
